import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, HeartHandshake } from "lucide-react";
import { SITE } from "@/lib/site";
import Reveal from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const GoogleG = ({ size = 20 }) => (
 <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
 <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
 <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
 <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
 <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
 </svg>
);

export const Hero = () => {
 return (
 <section className="relative overflow-hidden bg-white" data-testid="hero-section">
 <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-10 md:pt-20 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
 {/* Left */}
 <div className="lg:col-span-7">
 <Reveal>
 <div className="inline-flex items-center gap-2 border border-[#EAEAEA] bg-white rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
 <span className="w-1.5 h-1.5 rounded-full bg-[#FDD805]" />
 For First-Time Founders in Mumbai · Navi Mumbai · Thane
 </div>
 </Reveal>
 <Reveal delay={80}>
 <h1 className="font-display font-black tracking-tight text-[#0F1115] text-[42px] md:text-[64px] leading-[1.02] mt-6">
 Start your business with{" "}
 <span className="hl-yellow">confidence</span>, not confusion.
 </h1>
 </Reveal>
 <Reveal delay={160}>
 <p className="mt-6 text-[17px] md:text-[19px] leading-relaxed text-[#1C1F26] max-w-[640px]">
 We help First-Time Founders choose the right business structure,
 complete essential registrations and build the right foundation
 for launch with expert handholding at every step.
 </p>
 </Reveal>
 <Reveal delay={240}>
 <div className="mt-8 flex flex-wrap gap-3">
 <a
 href={SITE.whatsappUrl}
 target="_blank"
 rel="noreferrer"
 className="bg-[#25D366] text-white rounded-full px-7 py-3.5 font-bold text-[15px] hover:bg-[#1EBE57] hover:-translate-y-0.5 hover:shadow-lg transition-all inline-flex items-center gap-2"
 data-testid="hero-whatsapp-cta"
 >
 <WhatsAppIcon size={18} /> Chat
 </a>
 <Link
 to="/contact"
 className="bg-white text-[#0F1115] border-2 border-[#0F1115] rounded-full px-7 py-3 font-bold text-[15px] hover:bg-[#0F1115] hover:text-white transition-colors inline-flex items-center gap-2"
 data-testid="hero-enquiry-cta"
 >
 Submit Enquiry <ArrowRight size={16} />
 </Link>
 </div>
 </Reveal>

 <Reveal delay={320}>
 <div className="mt-10 flex flex-wrap items-center gap-4 text-[13px] text-[#4A4A4A]">
 <a
 href={SITE.googleReviewsUrl}
 target="_blank"
 rel="noreferrer noopener"
 className="group inline-flex items-center gap-2.5 bg-white border border-[#EAEAEA] rounded-full pl-3 pr-4 py-2 hover:border-[#0F1115] hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-all"
 data-testid="hero-google-rating-link"
 >
 <GoogleG size={16} />
 <div className="flex text-[#FDD805]">
 {[0, 1, 2, 3, 4].map((i) => (
 <Star key={i} size={12} fill="#FDD805" strokeWidth={0} />
 ))}
 </div>
 <div className="text-[12.5px] font-bold text-[#0F1115] leading-none">
 4.9
 <span className="text-[11px] font-normal text-[#4A4A4A] ml-1">
 · 251 Google Reviews
 </span>
 </div>
 </a>
 <div className="inline-flex items-center gap-2">
 <ShieldCheck size={15} className="text-[#0F1115]" />
 Founder-led guidance
 </div>
 <div className="inline-flex items-center gap-2">
 <HeartHandshake size={15} className="text-[#0F1115]" />
 Support beyond paperwork
 </div>
 </div>
 </Reveal>
 </div>

 {/* Right Bhavik photo with layered trust cards */}
 <div className="lg:col-span-5">
 <Reveal delay={200}>
 <div className="relative">
 {/* Decorative accents behind photo */}
 <div className="absolute -top-6 -right-4 w-40 h-40 rounded-full bg-[#FDD805] opacity-95 -z-0" aria-hidden />
 <div className="absolute -bottom-8 -left-6 w-28 h-28 rounded-2xl border-2 border-[#0F1115] -z-0" aria-hidden />

 {/* Photo */}
 <div className="relative rounded-3xl overflow-hidden border border-[#EAEAEA] bg-white z-10">
 <img
 src="/brand/bhavik.webp"
 alt="Bhavik Hariyani - Founder, HG Corporates"
 className="w-full h-[440px] md:h-[560px] object-cover object-top"
 data-testid="hero-founder-image"
 />
 </div>

 {/* Bhavik info card floating over bottom of photo */}
 <div className="absolute -bottom-5 left-4 right-4 bg-white border border-[#EAEAEA] rounded-2xl p-4 flex items-center gap-4 shadow-[0_10px_30px_rgba(0,0,0,0.08)] z-20">
 <div className="w-11 h-11 rounded-full bg-[#FDD805] flex items-center justify-center font-display font-black text-[#0F1115] text-[15px] shrink-0">
 BH
 </div>
 <div className="leading-tight min-w-0">
 <div className="font-bold text-[14px] text-[#0F1115] truncate">
 Bhavik Hariyani - Founder
 </div>
 <div className="text-[11px] text-[#4A4A4A] truncate">
 HG Corporates
 </div>
 </div>
 <div className="ml-auto shrink-0 hidden sm:block">
 <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4A4A4A]">
 Replies on
 </div>
 <div className="text-[12px] font-bold text-[#25D366] inline-flex items-center gap-1">
 <WhatsAppIcon size={12} /> WhatsApp
 </div>
 </div>
 </div>
 </div>
 </Reveal>
 </div>
 </div>
 </section>
 );
};

export default Hero;
